import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import * as actionCreators from "../../store/actions/index";
import { Button, Row, Col, Spin, Select, Card, message, Drawer } from 'antd';
import classes from './DispatchAddAsset.module.scss';
import StringFormatter from '../../shared/stringFormatter';
import Enums from '../../shared/enums';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTruck } from '@fortawesome/free-solid-svg-icons';
import { PlusOutlined } from '@ant-design/icons';
import { equalsIgnoreOrder, isBooleanTrue, isListNotEmpty, isObjectNotEmpty, isStringNotEmpty } from '../../shared/objectUtils';
import LoadStopUtils from '../../api/utils/loadStopUtils';
import LoadUtils from '../../api/utils/loadUtils';
import NewAsset from '../NewAsset/NewAsset';

const stringFormatter = new StringFormatter();

const DispatchAddAsset = ({ load, assets, bundledLoads, assetLoads, transportUnits, loadDriverIds, cancel, carrierId, isBundledLoad = false }) => {
    //#region constants

    const fullWidth = global.window.innerWidth;

    //#endregion
    //#region Enums

    const assetTypeOptions = Enums.AssetTypes.selectListOptions();

    //#endregion
    //#region useDispatch and useSelectors

    const dispatch = useDispatch();
    const isLoading = useSelector(state => state.loads.isRecordUpdateLoading);
    const error = useSelector(state => state.loads.updateRecordError);

    //#endregion
    //#region useStates

    const [selectedAssetTypes, setSelectedAssetTypes] = useState([]);
    const [assetMap, setAssetMap] = useState([]);
    const [filteredTransportUnits, setFilteredTransportUnits] = useState([]);
    const [showNewAsset, setShowNewAsset] = useState(false);

    //#endregion
    //#region toggles

    const toggleNewAsset = () => {
        setShowNewAsset(!showNewAsset);
    };

    //#endregion
    //#region asset methods

    const onAddAsset = (assetId) => {
        if (isStringNotEmpty(assetId) && isObjectNotEmpty(load)) {
            let loadAssetIds = isListNotEmpty(load.assetIds) ? load.assetIds : [];

            if (loadAssetIds.find(i => i === assetId) !== undefined) {
                message.error('The asset you chose is already assigned to this load.', 5);
                return;
            } else {
                loadAssetIds.push(assetId);
            }

            let payload = {
                assetIds: loadAssetIds
            };
            // console.log(payload);

            if (isBooleanTrue(isBundledLoad)) {
                dispatch(actionCreators.updateBundledLoad(load.id, payload, load));
            } else {
                dispatch(actionCreators.updateLoad(load.id, payload));
            }
        } else {
            onDone();
        }
    };

    const onAddTransportUnit = (assets) => {
        if (isListNotEmpty(assets) && isObjectNotEmpty(load)) {
            let loadAssetIds = isListNotEmpty(load.assetIds) ? load.assetIds : [];

            assets.forEach((asset) => {
                if (loadAssetIds.find(i => i === asset.id) === undefined) {
                    if (!loadAssetIds.includes(asset.id)) {
                        loadAssetIds.push(asset.id);
                    }
                }
            });

            let payload = {
                assetIds: loadAssetIds
            };
            // console.log(payload);

            if (isBooleanTrue(isBundledLoad)) {
                dispatch(actionCreators.updateBundledLoad(load.id, payload, load));
            } else {
                dispatch(actionCreators.updateLoad(load.id, payload));
            }
        } else {
            onDone();
        }
    };

    const onDone = () => {
        dispatch(actionCreators.updateLoadCancel());
        cancel();
    };

    //#endregion
    //#region useMemos

    useMemo(() => {
        // clear any previous errors if this is a new form
        dispatch(actionCreators.updateLoadErrorClear());
    }, []);

    useMemo(() => {
        if (isLoading !== null && isLoading === false && error === null) {
            onDone();
        }
    }, [isLoading, error]);

    useMemo(() => {
        if (isListNotEmpty(assets) && isObjectNotEmpty(load)) {
            let assetsArray = [];

            let existingLoadAssetIds = isListNotEmpty(load.assetIds) ? [...load.assetIds] : [];

            if (isListNotEmpty(selectedAssetTypes)) {
                if (isListNotEmpty(existingLoadAssetIds)) {
                    assets.filter(i => !existingLoadAssetIds.includes(i.id) && selectedAssetTypes.includes(i.assetType)).forEach((asset) => {
                        let assetBundledLoads = [];
                        let assetOtherLoads = [];
                        if (isListNotEmpty(bundledLoads)) {
                            bundledLoads.forEach((assetBundledLoad, loadIndex) => {
                                if (assetBundledLoad.isDeleted === false && LoadUtils.loadStatusIsNot(assetBundledLoad.loadStatus, ['CANCELLED']) && isListNotEmpty(assetBundledLoad.assetIds) && assetBundledLoad.assetIds.includes(asset.id)) {
                                    assetBundledLoad.loadName = LoadUtils.getChildLoadName(assetBundledLoad, loadIndex);
                                    assetBundledLoad.loadNumber = loadIndex + 1;
                                    if (assetBundledLoads.find(i => i.id === assetBundledLoad.id) === undefined) {
                                        assetBundledLoads.push(assetBundledLoad);
                                    }
                                }
                            });
                        }
                        if (isListNotEmpty(assetLoads)) {
                            assetLoads.forEach((assetOtherLoad) => {
                                if (assetOtherLoad.isDeleted === false && LoadUtils.loadStatusIsNot(assetOtherLoad.loadStatus, ['CANCELLED']) && isListNotEmpty(assetOtherLoad.assetIds) && assetOtherLoad.assetIds.includes(asset.id)) {
                                    if (assetOtherLoads.find(i => i.id === assetOtherLoad.id) === undefined) {
                                        assetOtherLoads.push(assetOtherLoad);
                                    }
                                }
                            });
                        }

                        assetsArray.push({
                            ...asset,
                            asset: asset,
                            assetBundledLoads: assetBundledLoads,
                            assetOtherLoads: assetOtherLoads
                        });
                    });
                } else {
                    assets.filter(i => selectedAssetTypes.includes(i.assetType)).forEach((asset) => {
                        let assetBundledLoads = [];
                        let assetOtherLoads = [];
                        if (isListNotEmpty(bundledLoads)) {
                            bundledLoads.forEach((assetBundledLoad, loadIndex) => {
                                if (assetBundledLoad.isDeleted === false && LoadUtils.loadStatusIsNot(assetBundledLoad.loadStatus, ['CANCELLED']) && isListNotEmpty(assetBundledLoad.assetIds) && assetBundledLoad.assetIds.includes(asset.id)) {
                                    assetBundledLoad.loadName = LoadUtils.getChildLoadName(assetBundledLoad, loadIndex);
                                    assetBundledLoad.loadNumber = loadIndex + 1;
                                    if (assetBundledLoads.find(i => i.id === assetBundledLoad.id) === undefined) {
                                        assetBundledLoads.push(assetBundledLoad);
                                    }
                                }
                            });
                        }
                        if (isListNotEmpty(assetLoads)) {
                            assetLoads.forEach((assetOtherLoad) => {
                                if (assetOtherLoad.isDeleted === false && LoadUtils.loadStatusIsNot(assetOtherLoad.loadStatus, ['CANCELLED']) && isListNotEmpty(assetOtherLoad.assetIds) && assetOtherLoad.assetIds.includes(asset.id)) {
                                    if (assetOtherLoads.find(i => i.id === assetOtherLoad.id) === undefined) {
                                        assetOtherLoads.push(assetOtherLoad);
                                    }
                                }
                            });
                        }

                        assetsArray.push({
                            ...asset,
                            asset: asset,
                            assetBundledLoads: assetBundledLoads,
                            assetOtherLoads: assetOtherLoads
                        });
                    });
                }
            } else {
                if (isListNotEmpty(existingLoadAssetIds)) {
                    assets.filter(i => !existingLoadAssetIds.includes(i.id)).forEach((asset) => {
                        let assetBundledLoads = [];
                        let assetOtherLoads = [];
                        if (isListNotEmpty(bundledLoads)) {
                            bundledLoads.forEach((assetBundledLoad, loadIndex) => {
                                if (assetBundledLoad.isDeleted === false && LoadUtils.loadStatusIsNot(assetBundledLoad.loadStatus, ['CANCELLED']) && isListNotEmpty(assetBundledLoad.assetIds) && assetBundledLoad.assetIds.includes(asset.id)) {
                                    assetBundledLoad.loadName = LoadUtils.getChildLoadName(assetBundledLoad, loadIndex);
                                    assetBundledLoad.loadNumber = loadIndex + 1;
                                    if (assetBundledLoads.find(i => i.id === assetBundledLoad.id) === undefined) {
                                        assetBundledLoads.push(assetBundledLoad);
                                    }
                                }
                            });
                        }
                        if (isListNotEmpty(assetLoads)) {
                            assetLoads.forEach((assetOtherLoad) => {
                                if (assetOtherLoad.isDeleted === false && LoadUtils.loadStatusIsNot(assetOtherLoad.loadStatus, ['CANCELLED']) && isListNotEmpty(assetOtherLoad.assetIds) && assetOtherLoad.assetIds.includes(asset.id)) {
                                    if (assetOtherLoads.find(i => i.id === assetOtherLoad.id) === undefined) {
                                        assetOtherLoads.push(assetOtherLoad);
                                    }
                                }
                            });
                        }

                        assetsArray.push({
                            ...asset,
                            asset: asset,
                            assetBundledLoads: assetBundledLoads,
                            assetOtherLoads: assetOtherLoads
                        });
                    });
                } else {
                    assets.forEach((asset) => {
                        let assetBundledLoads = [];
                        let assetOtherLoads = [];
                        if (isListNotEmpty(bundledLoads)) {
                            bundledLoads.forEach((assetBundledLoad, loadIndex) => {
                                if (assetBundledLoad.isDeleted === false && LoadUtils.loadStatusIsNot(assetBundledLoad.loadStatus, ['CANCELLED']) && isListNotEmpty(assetBundledLoad.assetIds) && assetBundledLoad.assetIds.includes(asset.id)) {
                                    assetBundledLoad.loadName = LoadUtils.getChildLoadName(assetBundledLoad, loadIndex);
                                    assetBundledLoad.loadNumber = loadIndex + 1;
                                    if (assetBundledLoads.find(i => i.id === assetBundledLoad.id) === undefined) {
                                        assetBundledLoads.push(assetBundledLoad);
                                    }
                                }
                            });
                        }
                        if (isListNotEmpty(assetLoads)) {
                            assetLoads.forEach((assetOtherLoad) => {
                                if (assetOtherLoad.isDeleted === false && LoadUtils.loadStatusIsNot(assetOtherLoad.loadStatus, ['CANCELLED']) && isListNotEmpty(assetOtherLoad.assetIds) && assetOtherLoad.assetIds.includes(asset.id)) {
                                    if (assetOtherLoads.find(i => i.id === assetOtherLoad.id) === undefined) {
                                        assetOtherLoads.push(assetOtherLoad);
                                    }
                                }
                            });
                        }

                        assetsArray.push({
                            ...asset,
                            asset: asset,
                            assetBundledLoads: assetBundledLoads,
                            assetOtherLoads: assetOtherLoads
                        });
                    });
                }
            }

            setAssetMap(assetsArray);
            // console.log(assetsArray);
        } else {
            setAssetMap([]);
        }
    }, [assets, bundledLoads, assetLoads, selectedAssetTypes, load]);

    useMemo(() => {
        console.log(transportUnits);
        console.log(loadDriverIds);
        // filter transport units if loadDriverIds is provided
        if (isListNotEmpty(transportUnits)) {
            if (isListNotEmpty(loadDriverIds)) {
                let transportUnitArray = transportUnits.filter(t => equalsIgnoreOrder(t.driverIds, loadDriverIds));
                setFilteredTransportUnits(transportUnitArray);
            } else {
                setFilteredTransportUnits([]);
            }
        } else {
            setFilteredTransportUnits([]);
        }
    }, [transportUnits, loadDriverIds]);

    //#endregion
    //#region display

    const getAssetLoadsDisplay = (asset, assetBundledLoads, assetOtherLoads) => {
        let assetLoadRows = [];
        let loadsToShow = [];

        if (isListNotEmpty(assetBundledLoads)) {
            assetBundledLoads.forEach((loadObj) => {
                if (loadsToShow.find(i => i.id === loadObj.id) === undefined && loadObj.id !== asset.id) {
                    loadsToShow.push(loadObj);
                }
            });
        }

        if (isListNotEmpty(assetOtherLoads)) {
            assetOtherLoads.forEach((loadObj) => {
                if (loadsToShow.find(i => i.id === loadObj.id) === undefined && loadObj.id !== asset.id) {
                    loadsToShow.push(loadObj);
                }
            });
        }

        if (isListNotEmpty(loadsToShow)) {
            loadsToShow.forEach((loadObj) => {
                let title = isStringNotEmpty(loadObj.loadName) ? loadObj.loadName : LoadUtils.getLoadName(loadObj);
                let time = LoadStopUtils.getOriginAndDestinationTimes(loadObj.origin, loadObj.destination);

                assetLoadRows.push(
                    <div key={`asset-load-${asset.id}-${loadObj.id}`}>
                        <span>{`${title} (${time})`}</span>
                    </div>
                );
            });
        }

        return (
            <div key={`asset-loads-${asset.id}`} style={{ marginTop: 24, marginBottom: 24 }}>
                <div><strong>Loads Assigned:</strong></div>
                {isListNotEmpty(assetLoadRows) ? assetLoadRows : <div><span>No Other Loads</span></div>}
            </div>
        );
    };

    const getTransportUnitLoadsDisplay = (loads, transportUnitIndex) => {
        let transportUnitLoadRows = [];
        let loadsToShow = [];

        if (isListNotEmpty(loads)) {
            loads.forEach((loadObj) => {
                if (loadsToShow.find(i => i.id === loadObj.id) === undefined) {
                    loadsToShow.push(loadObj);
                }
            });
        }

        if (isListNotEmpty(loadsToShow)) {
            loadsToShow.forEach((loadObj, index) => {
                let title = isStringNotEmpty(loadObj.loadName) ? loadObj.loadName : LoadUtils.getLoadName(loadObj);
                let time = LoadStopUtils.getOriginAndDestinationTimes(loadObj.origin, loadObj.destination);

                transportUnitLoadRows.push(
                    <div key={`transport-unit-load-${index}-${loadObj.id}`}>
                        <span>{`${title} (${time})`}</span>
                    </div>
                );
            });
        }

        return (
            <div key={`transport-unit-loads-${transportUnitIndex}`} style={{ marginTop: 12, marginBottom: 12 }}>
                <div><strong>Loads Assigned:</strong></div>
                {isListNotEmpty(transportUnitLoadRows) ? transportUnitLoadRows : <div><span>N/A</span></div>}
            </div>
        );
    };

    const getAssetDisplay = (asset, index) => {
        if (isObjectNotEmpty(asset)) {
            let assetType = stringFormatter.toFormattedString("AssetType", asset.assetType, null);
            let trailerType = (asset.assetType === 'TRAILER' || asset.assetType === 'COMBINED') ? (isObjectNotEmpty(asset.trailerType) ? asset.trailerType.description : '') : null;
            let licensePlate = `${asset.licensePlateNum} ${asset.licensePlateState}`;
            let assetName = isStringNotEmpty(asset.name) ? asset.name : null;

            return (
                <div key={`asset-row-${index}`} style={{ marginBottom: 4 }}>
                    <span><strong>{isStringNotEmpty(assetName) ? assetName + ', ' : ''}{isStringNotEmpty(licensePlate) ? licensePlate : ''}</strong><br /><i>Type: {assetType}{isStringNotEmpty(trailerType) ? ' - ' + trailerType : ''}</i></span>
                </div>
            );
        } else {
            return null;
        }
    };

    const getDriversDisplay = (drivers) => {
        let driverRows = [];
        if (isListNotEmpty(drivers)) {
            drivers.forEach((driver, index) => {
                driverRows.push(
                    <div key={`driver-row-${index}-${driver.id}`} style={{ marginBottom: 4 }}>
                        <span>{driver.firstName} {driver.lastName}</span>
                    </div>
                );
            });
        }

        return (
            <div style={{ marginTop: 12, marginBottom: 12 }}>
                <div><strong>Drivers Assigned to Asset(s):</strong></div>
                {isListNotEmpty(driverRows) ? driverRows : <div><span>N/A</span></div>}
            </div>
        );
    };

    //#endregion

    return (
        <Spin style={{ height: '100%', width: '100%' }} size="large" spinning={isLoading === true && error === null}>
            <Row gutter={[24, 24]}>
                {isListNotEmpty(filteredTransportUnits) ? (
                    <Col span={24}>
                        <div style={{ marginBottom: 12 }}><span><strong>Assets already associated with Driver(s)</strong></span></div>
                        {filteredTransportUnits.map((transportUnit, index) => {
                            return (
                                <Card
                                    style={{ marginBottom: 12, backgroundColor: '#F6FFED', border: '1px solid #b7eb8f', borderRadius: '15px' }}
                                    headStyle={{ borderBottom: '1px solid #b7eb8f' }}
                                    title={
                                        <Row gutter={[12, 12]}>
                                            {isListNotEmpty(transportUnit.assets) ? transportUnit.assets.map((asset, index) => {
                                                return (
                                                    <Col key={`asset-row-${index}`} span={24}>
                                                        <div style={{ display: 'flex' }}>
                                                            <div className={classes.iconContainer}><FontAwesomeIcon className={classes.icon} icon={faTruck} /></div>
                                                            <div className={classes.textContainer}>
                                                                {getAssetDisplay(asset, index)}
                                                            </div>
                                                        </div>
                                                    </Col>
                                                );
                                            }) : null}
                                        </Row>
                                    }
                                    extra={<Button type="primary" icon={<PlusOutlined />} shape="circle" onClick={(e) => { onAddTransportUnit(transportUnit.assets); }}></Button>}
                                >
                                    {getDriversDisplay(transportUnit.drivers)}
                                    {getTransportUnitLoadsDisplay(transportUnit.loads, index)}
                                </Card>
                            );
                        })}
                    </Col>
                ) : null}
                <Col span={24}>
                    <Select
                        placeholder="Asset Types: All"
                        mode="multiple"
                        allowClear={true}
                        style={{ width: '100%' }}
                        virtual={false}
                        onChange={(selected) => { setSelectedAssetTypes(selected); }}
                        showSearch={true}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {assetTypeOptions}
                    </Select>
                </Col>
                <Col span={24}>
                    {isListNotEmpty(assetMap) ? (
                        <>
                            <div style={{ height: 500, overflowY: 'auto' }}>
                                {assetMap.map((asset, index) => {
                                    return (
                                        <Card
                                            key={`dispatch-add-asset-card-${asset.id}`}
                                            style={{ marginBottom: 12, borderRadius: '15px' }}
                                            title={
                                                <div style={{ display: 'flex' }}>
                                                    <div className={classes.iconContainer}><FontAwesomeIcon className={classes.icon} icon={faTruck} /></div>
                                                    <div className={classes.textContainer}>
                                                        {getAssetDisplay(asset, index)}
                                                    </div>
                                                </div>
                                            }
                                            extra={<Button type="primary" icon={<PlusOutlined />} shape="circle" onClick={(e) => { onAddAsset(asset.id); }}></Button>}
                                        >
                                            <div><span><strong>Available: {isObjectNotEmpty(asset) ? stringFormatter.toYesNo(asset.isAvailable) : 'No'}</strong></span></div>
                                            {getAssetLoadsDisplay(asset, asset.assetBundledLoads, asset.assetOtherLoads)}
                                        </Card>
                                    );
                                })}
                            </div>
                        </>
                    ) : null}
                </Col>
                <Col span={24}>
                    <b>Asset Doesn't Exist Yet?</b>
                    <br />
                    <Button type='primary' onClick={toggleNewAsset}>Add New Asset to Account</Button>
                </Col>
            </Row>
            <Drawer
                title={'Add New Asset'}
                width={fullWidth > 720 ? fullWidth / 2 : 360}
                onClose={toggleNewAsset}
                visible={showNewAsset}
                bodyStyle={{ paddingBottom: 80 }}
                style={{ zIndex: 1000 }}
                destroyOnClose={true}
                closable={false}
                maskClosable={false}
            >
                <NewAsset cancel={toggleNewAsset} accountId={carrierId} />
            </Drawer>
        </Spin>
    );
};

export default DispatchAddAsset;